import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { createSafeCloseHandler } from '../../utils/modalUtils';

interface AddEscalationLevelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddLevel: (levelData: any) => Promise<void>;
  projectId: string;
}

interface LevelFormData {
  name: string;
  description: string;
  priority: number;
  response_time: number;
  escalation_threshold: number;
  notification_methods: string[];
}

const NOTIFICATION_METHODS = [
  'email',
  'sms',
  'phone_call',
  'messaging',
  'push_notification'
];

export const AddEscalationLevelModal: React.FC<AddEscalationLevelModalProps> = ({
  isOpen,
  onClose,
  onAddLevel
}) => {
  const [formData, setFormData] = useState<LevelFormData>({
    name: '',
    description: '',
    priority: 1,
    response_time: 60, // in minutes
    escalation_threshold: 240, // in minutes
    notification_methods: ['email']
  });
  const [isLoading, setIsLoading] = useState(false);

  // Create safe close handler to prevent accidental loss of data
  const handleSafeClose = createSafeCloseHandler(
    formData,
    onClose,
    ['priority', 'response_time', 'escalation_threshold', 'notification_methods'] // Exclude auto-filled fields
  );

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleSafeClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, handleSafeClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'priority' || name === 'response_time' || name === 'escalation_threshold' 
        ? parseInt(value, 10) || 0
        : value
    }));
  };

  const handleNotificationMethodChange = (method: string, checked: boolean) => {
    // Only allow email to be changed
    if (method !== 'email') {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      notification_methods: checked
        ? [...prev.notification_methods, method]
        : prev.notification_methods.filter(m => m !== method)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.description) {
      toast.error('Please fill in all required fields (Name, Description)');
      return;
    }

    if (formData.notification_methods.length === 0) {
      toast.error('Please select at least one notification method');
      return;
    }

    if (formData.priority < 1) {
      toast.error('Priority must be at least 1');
      return;
    }

    setIsLoading(true);
    try {
      console.log('AddEscalationLevelModal: submitting level data', formData);
      console.log('AddEscalationLevelModal: form data types:', {
        name: typeof formData.name,
        description: typeof formData.description,
        priority: typeof formData.priority,
        response_time: typeof formData.response_time,
        escalation_threshold: typeof formData.escalation_threshold,
        notification_methods: typeof formData.notification_methods,
        notification_methods_array: Array.isArray(formData.notification_methods)
      });
      
      await onAddLevel(formData);
      
      console.log('AddEscalationLevelModal: level added successfully');
      toast.success('Escalation level added successfully');
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        priority: 1,
        response_time: 60,
        escalation_threshold: 240,
        notification_methods: ['email']
      });
      
      onClose();
    } catch (error) {
      console.error('AddEscalationLevelModal: failed to add level', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add escalation level';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50"
      onClick={(e) => {
        // Prevent this overlay click from bubbling to parent modals
        e.stopPropagation();
        handleSafeClose();
      }}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleSafeClose();
        }}
        className="fixed top-4 right-4 z-[60] text-[#aaa] hover:text-[#555] p-2"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div 
        className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[#eaeaea]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4">
          <h2 className="text-xl font-semibold">Add Escalation Level</h2>
          <p className="text-sm text-[#555] mt-1">
            Create a new escalation level for this project
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., Level 1, Project Manager"
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Priority <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                min="1"
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                required
              />
              <p className="text-xs text-[#888] mt-1">Lower numbers = higher priority</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#444] mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              placeholder="Describe the role and responsibilities of this escalation level"
              className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              required
            />
          </div>

          {/* Timing Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Response Time (minutes)
              </label>
              <input
                type="number"
                name="response_time"
                value={formData.response_time}
                onChange={handleInputChange}
                min="1"
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              />
              <p className="text-xs text-[#888] mt-1">Expected response time for this level</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Escalation Threshold (minutes)
              </label>
              <input
                type="number"
                name="escalation_threshold"
                value={formData.escalation_threshold}
                onChange={handleInputChange}
                min="1"
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              />
              <p className="text-xs text-[#888] mt-1">When to escalate to next level if unresolved</p>
            </div>
          </div>

          {/* Notification Methods */}
          <div>
            <label className="block text-sm font-medium text-[#444] mb-2">
              Notification Methods <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {NOTIFICATION_METHODS.map((method) => {
                const isDisabled = method !== 'email';
                return (
                  <label key={method} className={`flex items-center space-x-2 ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={formData.notification_methods.includes(method)}
                    onChange={(e) => handleNotificationMethodChange(method, e.target.checked)}
                      disabled={isDisabled}
                      className="rounded border-[#eaeaea] focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <span className="text-sm text-[#444] capitalize">
                    {method.replace('_', ' ')}
                      {isDisabled && <span className="text-xs text-[#888] ml-1">(Coming soon)</span>}
                  </span>
                </label>
                );
              })}
            </div>
            <p className="text-xs text-[#888] mt-1">Select how to notify this escalation level. Only email is currently available.</p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleSafeClose}
              className="px-4 py-2 text-[#555] border border-[#eaeaea] rounded-lg hover:bg-[#fafafa]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.name || !formData.description || formData.notification_methods.length === 0}
              className="px-4 py-2 bg-[#171717] text-white rounded-md hover:bg-[#333] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Adding...' : 'Add Level'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 